import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal
} from '@angular/core';
import { SnackBarService } from '../snack-bar.service';
import { Router } from '@angular/router';
import { PeriodicityPricing } from '../../../core/api/models/periodicity-pricing';
import { INITIAL_VALUE_PRICING } from '../../../view/tarifications/tarifications.component';
import { catchError, finalize, of } from 'rxjs';
import { SouscriptionClientViewModel } from '../../../view/souscription/souscription.component';
import { FormGroup } from '@angular/forms';
import {
  AcsBeneficiaryClientModel,
  SaveExpatProjectModel
} from '../../model/acs-project.model';
import { Routing } from '../../../app.routes';
import { AuthStateService } from '../state/auth-state.service';
import { ProjectStateService } from '../state/project-state.service';
import { ReferenceStateService } from '../state/reference-state.service';
import { ExpatClient } from '../../../core/api/models/expat-client';
import { SubscriptionFeatureService } from '../feature/subscription-feature.service';
import { Beneficiary } from '../../../core/api/models/beneficiary';

@Injectable({
  providedIn: 'root'
})
export class SouscriptionComponentService {
  private authStateService: AuthStateService = inject(AuthStateService);
  private projectStateService: ProjectStateService =
    inject(ProjectStateService);
  private referenceStateService: ReferenceStateService = inject(
    ReferenceStateService
  );
  private snackBarService: SnackBarService = inject(SnackBarService);
  private router: Router = inject(Router);
  readonly subscriptionFeatureService: SubscriptionFeatureService = inject(
    SubscriptionFeatureService
  );

  public isLoading: WritableSignal<boolean> = signal(false);
  public periodicityPricing: WritableSignal<PeriodicityPricing> = signal(
    INITIAL_VALUE_PRICING
  );

  /** state select **/
  public selectIsLoading() {
    return this.isLoading;
  }

  /** Getter **/

  public getPricingInformations() {
    return this.projectStateService.getPricingInformations();
  }

  public getExpatClientInfos() {
    return this.projectStateService.selectExpatClientInfos();
  }

  public getExpatPricing() {
    return this.projectStateService.selectExpatPricing();
  }

  public getNationalitiesCountries() {
    return this.referenceStateService.selectNationalityCountries();
  }

  public getBusinessSector() {
    return this.referenceStateService.selectBusinessSector();
  }

  public getExpatCountry(
    clientViewModel: SouscriptionClientViewModel,
    form: FormGroup
  ) {
    return computed(() => {
      const natCountry = this.getNationalitiesCountries();
      const country = natCountry().find(
        (c) => c.alpha3 === clientViewModel.country
      );
      const nat = natCountry().find(
        (c) => c.alpha3 === clientViewModel.birthCountry
      );
      if (country) {
        form.controls['country'].setValue(country.alpha3);
      }
      if (nat) {
        return nat!.label;
      }
      return '';
    });
  }

  /** Set and Update **/

  public updateExpatClientInfos(
    infoToUpdate: Partial<AcsBeneficiaryClientModel>,
    forceUpdateBeneficiaries: boolean = false
  ) {
    this.projectStateService.updateExpatClientInfos(
      infoToUpdate,
      forceUpdateBeneficiaries
    );
  }

  public setProjectBeneficiaries(
    beneficiaries: Beneficiary[] | undefined
  ) {
    this.projectStateService.setProjectBeneficiaries(beneficiaries);
  }

  public formatForSave(
    clientInfo: ExpatClient,
    form: FormGroup
  ): SaveExpatProjectModel {
    //const clientInfo = this.getExpatClientInfos();
    if (
      this.getPricingInformations()!.expat_project_additional_country === '0'
    ) {
      delete this.getPricingInformations()!.expat_project_additional_country;
    }
    if (
      this.getPricingInformations()!.expat_project_maternity_option?.toString() ===
      '0'
    ) {
      delete this.getPricingInformations()!.expat_project_maternity_option;
    }
    const projet: SaveExpatProjectModel = {
      pricing: {
        ...this.getPricingInformations()!,
        expat_project_periodicity_choice: parseInt(
          form.controls['expat_project_periodicity_choice'].value
        ),
        expat_project_payment_method_choice:
          form.controls['expat_project_payment_method_choice'].value
      },
      subscription: true,

      producer_code: this.authStateService.selectProducerCode()() ?? undefined,
      product: this.getPricingInformations()!.currency,
      beneficiaries: form.controls['beneficiaries'].value,
      mode: this.authStateService.selectAcsMode()(),
      client: {
        title: clientInfo.title,
        last_name: clientInfo.last_name,
        first_name: clientInfo.first_name,
        birth_date: clientInfo.birth_date,
        address_1: form.controls['address_1'].value,
        address_2: form.controls['address_2'].value,
        country: form.controls['country'].value,
        phone: form.controls['phone'].value,
        city: form.controls['city'].value,
        email: form.controls['email'].value,
        postal_code: form.controls['postal_code'].value,
        language: clientInfo.language,
        terms_accepted: clientInfo.terms_accepted,
        data_consent_commercial: clientInfo.data_consent_commercial
      }
    };
    if (projet.client) {
      // Le bénéficiaire maternité a été désigné à l'étape 3 : on conserve le marqueur du souscripteur tel
      // qu'enregistré, sans le déduire du niveau d'option maternité retenu sur le projet.
      const storedSouscripteur = this.getProjectBeneficiaries().find(
        (b) => b.category === 'Souscripteur'
      );
      const souscripteur: Beneficiary = {
        category: 'Souscripteur',
        birth_date: projet.client.birth_date,
        acs_expat_beneficiary_business_sector:
          form.controls['acs_expat_beneficiary_business_sector'].value,
        acs_expat_beneficiary_email: form.controls['email'].value,
        acs_expat_beneficiary_maternity_option:
          storedSouscripteur?.acs_expat_beneficiary_maternity_option ?? 0,
        acs_expat_beneficiary_nationality_country:
          this.getPricingInformations()!.expat_project_nationality,
        acs_expat_beneficiary_occupation:
          form.controls['acs_expat_beneficiary_occupation'].value,
        acs_expat_beneficiary_ppe: form.controls['politiquementExposee'].value,
        first_name: projet.client.first_name,
        last_name: projet.client.last_name
      };
      const indexSouscripteur = projet.beneficiaries?.findIndex(
        (b) => b.category === 'Souscripteur'
      );
      // ICI on ajoute le client a la liste de beneficiaires avec le status Souscripteur
      if (indexSouscripteur !== undefined && indexSouscripteur === -1) {
        projet.beneficiaries?.push(souscripteur);
      } else if (indexSouscripteur !== undefined && indexSouscripteur !== -1) {
        projet.beneficiaries![indexSouscripteur] = souscripteur;
      }
    }
    return projet;
  }

  public submitStep4(form: FormGroup) {
    const clientInfo = this.getExpatClientInfos();
    // on verifie que tout les objet sont bien rempli dans le global store
    if (
      this.getPricingInformations() &&
      this.authStateService.getSessionStorageUrlParamInfo() &&
      clientInfo
    ) {
      const projet = this.formatForSave(clientInfo, form);

      this.setProjectBeneficiaries(projet.beneficiaries);
      this.projectStateService.updateClient(projet.client!);
      // validateStep4() reconstruit la requête depuis le store : sans cette mise à jour, la périodicité
      // et le mode de paiement saisis à cette étape ne sont jamais transmis au back.
      if (projet.pricing) {
        this.projectStateService.updatePricingInformations(projet.pricing);
      }
      const token = this.authStateService.selectToken();
      //On verifie la presence du token et de l'uuid du projet avant la mise a jour
      //TODO Gerer le cas d'erreur ?
      if (token()) {
        this.isLoading.set(true);
        this.subscriptionFeatureService
          .validateStep4()
          .pipe(
            finalize(() => this.isLoading.set(false)),
            catchError((err) => {
              this.snackBarService.errorMessage(err.error.message);
              return of(null);
            })
          )
          .subscribe({
            next: (res) => {
              if (res) {
                this.projectStateService.setIsNewClient(false);
                this.router.navigate([Routing.CONFIRMATIONSOUSCRIPTION]).then();
              }
            }
          });
      }
    }
  }

  getProjectBeneficiaries() {
    return this.projectStateService.selectProjectBeneficiaries();
  }
}
